<template>
  <div class="page-container">
    <el-card style="margin-bottom: 20px;">
      <div slot="header" class="clearfix">
        <span style="font-size: 18px; font-weight: bold;">海报生成器</span>
      </div>
      
      <el-row :gutter="20">
        <el-col :span="12">
          <el-card shadow="hover">
            <div slot="header">
              <span>海报配置</span>
            </div>
            
            <el-form :model="posterForm" label-width="100px">
              <el-form-item label="海报模板">
                <el-select v-model="posterForm.templateId" placeholder="请选择模板" style="width: 100%;">
                  <el-option 
                    v-for="template in templates" 
                    :key="template.id" 
                    :label="template.name" 
                    :value="template.id">
                  </el-option>
                </el-select>
              </el-form-item>
              
              <el-form-item label="海报标题">
                <el-input v-model="posterForm.title" placeholder="请输入海报标题"></el-input>
              </el-form-item>
              
              <el-form-item label="AI生成">
                <el-input 
                  v-model="aiPrompt" 
                  type="textarea"
                  :rows="3"
                  placeholder="描述你想要的海报效果，例如：一张现代简约风格的科技产品推广海报，蓝色主题，突出创新">
                </el-input>
                <el-button 
                  type="primary" 
                  @click="generateByAI" 
                  :loading="aiGenerating"
                  icon="el-icon-magic-stick"
                  style="margin-top: 10px; width: 100%;">
                  AI智能生成海报配置
                </el-button>
              </el-form-item>
              
              <el-form-item label="副标题">
                <el-input v-model="posterForm.subtitle" placeholder="请输入副标题"></el-input>
              </el-form-item>
              
              <el-form-item label="背景图片">
                <el-upload
                  class="bg-uploader"
                  :action="uploadUrl"
                  :headers="uploadHeaders"
                  :show-file-list="false"
                  :on-success="handleBgSuccess"
                  :before-upload="beforeUpload">
                  <img v-if="posterForm.backgroundImage" :src="posterForm.backgroundImage" class="bg-image">
                  <i v-else class="el-icon-plus bg-uploader-icon"></i>
                </el-upload>
                <div class="tip">建议尺寸: 750x1334px</div>
              </el-form-item>
              
              <el-form-item label="二维码内容">
                <el-input v-model="posterForm.qrcodeContent" placeholder="输入链接或文本"></el-input>
              </el-form-item>
              
              <el-form-item label="主题色">
                <el-color-picker v-model="posterForm.themeColor"></el-color-picker>
              </el-form-item>
              
              <el-form-item label="文字颜色">
                <el-color-picker v-model="posterForm.textColor"></el-color-picker>
              </el-form-item>
              
              <el-form-item>
                <el-button 
                  type="primary" 
                  @click="generatePoster" 
                  :loading="generating"
                  icon="el-icon-picture">
                  生成海报
                </el-button>
                <el-button @click="resetForm">重置</el-button>
              </el-form-item>
            </el-form>
          </el-card>
        </el-col>
        
        <el-col :span="12">
          <el-card shadow="hover">
            <div slot="header">
              <span>预览效果</span>
              <el-button 
                v-if="generatedPoster" 
                style="float: right; padding: 3px 8px" 
                type="primary" 
                size="small"
                @click="downloadPoster">
                <i class="el-icon-download"></i> 下载海报
              </el-button>
            </div>
            
            <div class="preview-container">
              <!-- 隐藏canvas,始终渲染供绘制使用 -->
              <canvas ref="posterCanvas" :width="750" :height="1334" style="display: none;"></canvas>
              
              <div v-if="!generatedPoster" class="empty-preview">
                <i class="el-icon-picture-outline"></i>
                <p>配置参数后点击生成海报</p>
              </div>
              <div v-else class="poster-preview">
                <img :src="generatedPoster" class="poster-image" />
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </el-card>
    
    <!-- 历史记录 -->
    <el-card>
      <div slot="header">
        <span>历史海报</span>
      </div>
      
      <el-table :data="historyList" border v-loading="loading">
        <el-table-column prop="title" label="标题" width="200"></el-table-column>
        <el-table-column label="预览" width="120">
          <template slot-scope="scope">
            <img :src="scope.row.posterUrl" class="history-thumb" @click="previewPoster(scope.row)" />
          </template>
        </el-table-column>
        <el-table-column label="模板" width="150">
          <template slot-scope="scope">
            {{ getTemplateName(scope.row.templateId) }}
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="180"></el-table-column>
        <el-table-column label="操作" fixed="right" width="200" align="center">
          <template slot-scope="scope">
            <el-button size="mini" @click="usePoster(scope.row)">使用</el-button>
            <el-button size="mini" type="primary" @click="downloadHistoryPoster(scope.row)">下载</el-button>
            <el-button size="mini" type="danger" @click="deletePoster(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <el-pagination
        @current-change="handlePageChange"
        :current-page="page.current"
        :page-size="page.size"
        layout="total, prev, pager, next"
        :total="page.total"
        style="margin-top: 20px; text-align: right;">
      </el-pagination>
    </el-card>
    
    <!-- 预览对话框 -->
    <el-dialog 
      :visible.sync="previewVisible" 
      width="50%"
      :show-close="true">
      <img :src="previewUrl" style="width: 100%;" />
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'PosterGenerate',
  data() {
    return {
      posterForm: {
        templateId: null,
        title: '',
        subtitle: '',
        backgroundImage: '',
        qrcodeContent: '',
        themeColor: '#409EFF',
        textColor: '#ffffff'
      },
      templates: [
        { id: 1, name: '简约风格' },
        { id: 2, name: '商务风格' },
        { id: 3, name: '活动风格' },
        { id: 4, name: '促销风格' }
      ],
      generating: false,
      generatedPoster: null,
      aiPrompt: '',
      aiGenerating: false,
      loading: false,
      historyList: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      previewVisible: false,
      previewUrl: '',
      uploadUrl: process.env.VUE_APP_API_BASE_URL + '/admin/upload/image',
      uploadHeaders: {
        'Authorization': 'Bearer ' + localStorage.getItem('admin_token')
      }
    }
  },
  mounted() {
    this.loadHistory()
  },
  methods: {
    async generatePoster() {
      if (!this.posterForm.title) {
        this.$message.warning('请输入海报标题')
        return
      }
      
      this.generating = true
      try {
        // 等待DOM更新后再绘制
        await this.$nextTick()
        
        // 使用Canvas绘制海报
        await this.drawPoster()
        
        // 将Canvas转为Blob并上传
        const posterUrl = await this.uploadPosterImage()
        
        // 保存到服务器
        const res = await this.$axios.post('/admin/poster/generate', {
          ...this.posterForm,
          posterUrl: posterUrl
        })
        
        if (res.data.code === 200) {
          this.$message.success('海报生成成功')
          this.loadHistory()
        }
      } catch (error) {
        this.$message.error('生成失败: ' + (error.message || error))
        console.error(error)
      } finally {
        this.generating = false
      }
    },
    async generateByAI() {
      if (!this.aiPrompt) {
        this.$message.warning('请输入AI生成提示词')
        return
      }
      
      this.aiGenerating = true
      try {
        const res = await this.$axios.post('/admin/poster/ai-generate', {
          prompt: this.aiPrompt
        })
        
        if (res.data.code === 200) {
          const aiConfig = res.data.data
          // 应用AI生成的配置
          this.posterForm.title = aiConfig.title || this.posterForm.title
          this.posterForm.subtitle = aiConfig.subtitle || this.posterForm.subtitle
          this.posterForm.themeColor = aiConfig.themeColor || this.posterForm.themeColor
          this.posterForm.textColor = aiConfig.textColor || this.posterForm.textColor
          
          this.$message.success('AI配置生成成功，请点击“生成海报”查看效果')
        }
      } catch (error) {
        this.$message.error('AI生成失败: ' + (error.message || error))
        console.error(error)
      } finally {
        this.aiGenerating = false
      }
    },
    async drawPoster() {
      return new Promise((resolve, reject) => {
        const canvas = this.$refs.posterCanvas
        
        // 检查canvas是否存在
        if (!canvas) {
          reject(new Error('Canvas元素未找到'))
          return
        }
        
        const ctx = canvas.getContext('2d')
        
        // 清空画布
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        
        // 绘制背景
        if (this.posterForm.backgroundImage) {
          const bgImg = new Image()
          bgImg.crossOrigin = 'Anonymous'
          bgImg.onload = () => {
            ctx.drawImage(bgImg, 0, 0, canvas.width, canvas.height)
            this.drawContent(ctx, canvas)
            resolve()
          }
          bgImg.src = this.posterForm.backgroundImage
        } else {
          // 使用纯色背景
          ctx.fillStyle = this.posterForm.themeColor
          ctx.fillRect(0, 0, canvas.width, canvas.height)
          this.drawContent(ctx, canvas)
          resolve()
        }
      })
    },
    drawContent(ctx, canvas) {
      // 绘制标题
      ctx.fillStyle = this.posterForm.textColor
      ctx.font = 'bold 60px Arial'
      ctx.textAlign = 'center'
      ctx.fillText(this.posterForm.title, canvas.width / 2, 200)
      
      // 绘制副标题
      if (this.posterForm.subtitle) {
        ctx.font = '40px Arial'
        ctx.fillText(this.posterForm.subtitle, canvas.width / 2, 280)
      }
      
      // 绘制二维码区域 (这里用矩形模拟，实际应该生成真实二维码)
      if (this.posterForm.qrcodeContent) {
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(canvas.width / 2 - 100, canvas.height - 300, 200, 200)
        ctx.fillStyle = '#333333'
        ctx.font = '24px Arial'
        ctx.fillText('扫码查看', canvas.width / 2, canvas.height - 80)
      }
      
      // 转换为图片用于预览
      this.generatedPoster = canvas.toDataURL('image/png')
    },
    async uploadPosterImage() {
      return new Promise((resolve, reject) => {
        const canvas = this.$refs.posterCanvas
        
        // 将Canvas转为Blob
        canvas.toBlob(async (blob) => {
          if (!blob) {
            reject(new Error('图片转换失败'))
            return
          }
          
          try {
            // 创建FormData上传
            const formData = new FormData()
            formData.append('file', blob, `poster_${Date.now()}.png`)
            
            // 上传到服务器，使用正确的上传接口
            const res = await this.$axios.post('/upload/image', formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            })
            
            if (res.data.code === 200) {
              resolve(res.data.data.url)
            } else {
              reject(new Error(res.data.message || '上传失败'))
            }
          } catch (error) {
            reject(error)
          }
        }, 'image/png', 0.9)
      })
    },
    resetForm() {
      this.posterForm = {
        templateId: null,
        title: '',
        subtitle: '',
        backgroundImage: '',
        qrcodeContent: '',
        themeColor: '#409EFF',
        textColor: '#ffffff'
      }
      this.generatedPoster = null
    },
    downloadPoster() {
      const link = document.createElement('a')
      link.download = `poster_${Date.now()}.png`
      link.href = this.generatedPoster
      link.click()
    },
    async loadHistory() {
      this.loading = true
      try {
        const res = await this.$axios.get('/admin/poster/list', {
          params: {
            pageNum: this.page.current,
            pageSize: this.page.size
          }
        })
        if (res.data.code === 200) {
          this.historyList = res.data.data.records || []
          this.page.total = res.data.data.total || 0
        }
      } catch (error) {
        console.error('加载历史记录失败:', error)
      } finally {
        this.loading = false
      }
    },
    handlePageChange(page) {
      this.page.current = page
      this.loadHistory()
    },
    previewPoster(row) {
      this.previewUrl = row.posterUrl
      this.previewVisible = true
    },
    usePoster(row) {
      this.posterForm = {
        templateId: row.templateId,
        title: row.title,
        subtitle: row.subtitle,
        backgroundImage: row.backgroundImage,
        qrcodeContent: row.qrcodeContent,
        themeColor: row.themeColor,
        textColor: row.textColor
      }
      this.generatedPoster = row.posterUrl
      window.scrollTo(0, 0)
    },
    downloadHistoryPoster(row) {
      const link = document.createElement('a')
      link.download = `${row.title}_${Date.now()}.png`
      link.href = row.posterUrl
      link.click()
    },
    async deletePoster(row) {
      this.$confirm('确认删除该海报?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await this.$axios.delete(`/admin/poster/delete/${row.id}`)
          if (res.data.code === 200) {
            this.$message.success('删除成功')
            this.loadHistory()
          }
        } catch (error) {
          this.$message.error('删除失败')
        }
      }).catch(() => {})
    },
    getTemplateName(templateId) {
      const template = this.templates.find(t => t.id === templateId)
      return template ? template.name : '-'
    },
    handleBgSuccess(res) {
      if (res.code === 200) {
        this.posterForm.backgroundImage = res.data.url
      }
    },
    beforeUpload(file) {
      const isImage = file.type.indexOf('image') !== -1
      const isLt5M = file.size / 1024 / 1024 < 5

      if (!isImage) {
        this.$message.error('只能上传图片文件!')
      }
      if (!isLt5M) {
        this.$message.error('图片大小不能超过 5MB!')
      }
      return isImage && isLt5M
    }
  }
}
</script>

<style scoped>
.page-container {
  padding: 20px;
}

.preview-container {
  min-height: 600px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
  border-radius: 4px;
}

.empty-preview {
  text-align: center;
  color: #909399;
}

.empty-preview i {
  font-size: 80px;
  margin-bottom: 20px;
}

.empty-preview p {
  font-size: 14px;
}

.poster-preview {
  width: 100%;
  text-align: center;
}

.poster-image {
  max-width: 100%;
  max-height: 600px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

.bg-uploader {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  display: inline-block;
}

.bg-uploader:hover {
  border-color: #409EFF;
}

.bg-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 178px;
  height: 178px;
  line-height: 178px;
  text-align: center;
  display: block;
}

.bg-image {
  width: 178px;
  height: 178px;
  display: block;
  object-fit: cover;
}

.tip {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
}

.history-thumb {
  width: 80px;
  height: auto;
  cursor: pointer;
  border-radius: 4px;
}

.history-thumb:hover {
  opacity: 0.8;
}
</style>
